<script setup lang="ts">
import type { CalendarRootEmits, CalendarRootProps } from 'reka-ui'
import type { HTMLAttributes, Ref } from 'vue'
import { getLocalTimeZone, today } from '@internationalized/date'
import { reactiveOmit } from '@vueuse/core'
import { CalendarRoot, useDateFormatter, useForwardPropsEmits } from 'reka-ui'
import { computed, ref, watch } from 'vue'
import { cn } from '@ds/lib/utils'
import {
  CalendarCell,
  CalendarCellTrigger,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHead,
  CalendarGridRow,
  CalendarHeadCell,
  CalendarHeader,
  CalendarHeading,
} from '../../primitives/calendar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../primitives/select'

/*
 * Reconciled from the shadcn-vue `CalendarWithSelect` example, which is the
 * upstream answer to "let me jump to a month or year" — it swaps the static
 * heading for two selects driving CalendarRoot's `placeholder`.
 *
 * The one departure is styling: upstream renders two obvious form selects,
 * where this keeps them reading as the heading — bold, borderless, sized to
 * their text — so "November 2019" still looks like a title and simply happens
 * to be clickable, which is what the legacy UIDatePicker's heading does.
 *
 * Lives beside DSDateField rather than in primitives/calendar: upstream ships
 * this as `registry:example`, not `registry:ui`, so there is nothing to keep
 * diffable and primitives/ must stay vendored.
 */
const props = withDefaults(
  defineProps<CalendarRootProps & { class?: HTMLAttributes['class'] }>(),
  {
    modelValue: undefined,
    weekdayFormat: 'short',
  },
)
const emits = defineEmits<CalendarRootEmits & {
  /**
   * True while either heading select is open. The popover this calendar lives
   * in must not dismiss during that time — see DSDateField.
   *
   * Property syntax, not a call signature: CalendarRootEmits uses the former
   * and the SFC compiler refuses a mix of the two.
   */
  menuOpenChange: [open: boolean]
}>()

// `class` and `placeholder` are handled here, so they must not be forwarded —
// v-bind after v-model:placeholder would overwrite the two-way binding.
const delegatedProps = reactiveOmit(props, 'class', 'placeholder')

const forwarded = useForwardPropsEmits(delegatedProps, emits)

/*
 * The month being browsed, which is NOT the selected date.
 *
 * Upstream's example ties this to modelValue with useVModel — fine for a
 * standalone demo, wrong here: changing month or year would emit
 * update:modelValue, so the field read it as the user picking a date, wrote a
 * value nobody chose, and closed the popover mid-selection.
 *
 * So it is local state. It follows the selection when one arrives, and is never
 * emitted; CalendarRoot still emits update:modelValue on its own when a day is
 * actually clicked.
 */
/*
 * Typed from CalendarRootProps rather than from DateValue directly. The
 * DateValue our @internationalized/date exports and the one reka-ui's types
 * were built against are structurally identical but not the same identity, so
 * a DateValue ref is rejected by reka's own placeholder prop. Deriving the type
 * from the prop it feeds removes the question.
 */
type PlaceholderValue = NonNullable<CalendarRootProps['placeholder']>

// modelValue is DateValue | DateValue[] to cover multiple-selection mode,
// which this field never uses.
const selected = computed(() =>
  (Array.isArray(props.modelValue)
    ? props.modelValue[0]
    : props.modelValue) as PlaceholderValue | undefined,
)

const placeholder = ref(
  selected.value ?? (today(getLocalTimeZone()) as PlaceholderValue),
) as Ref<PlaceholderValue>

watch(selected, value => {
  if (value) placeholder.value = value
})

// In script rather than inline: `.set()` widens across the DateValue union, so
// the result needs narrowing that reads badly in a template expression.
const setMonth = (value: unknown) => {
  const month = Number(value)
  if (!month || month === placeholder.value.month) return
  placeholder.value = placeholder.value.set({ month }) as PlaceholderValue
}

const setYear = (value: unknown) => {
  const year = Number(value)
  if (!year || year === placeholder.value.year) return
  placeholder.value = placeholder.value.set({ year }) as PlaceholderValue
}

const formatter = useDateFormatter('en')

const isMonthOpen = ref(false)
const isYearOpen = ref(false)

watch(
  () => isMonthOpen.value || isYearOpen.value,
  open => emits('menuOpenChange', open),
)

/*
 * Upstream builds these with createYear/createDecade from `reka-ui/date`. That
 * subpath is unreachable here: tsconfig sets moduleResolution "node", which
 * predates package `exports` and cannot see it. Deep-importing `reka-ui/dist/date`
 * would work and would be fragile, and switching resolution repo-wide is not
 * this component's call — so the two ranges are built directly. Neither is date
 * arithmetic; they are a fixed 1-12 and a numeric span.
 */
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => ({
  value: index + 1,
  // Any year works — only the month name is read, and the formatter localises it.
  label: formatter.custom(new Date(2000, index, 1), { month: 'long' }),
}))

/*
 * A century back, ten forward. Upstream offers ±10, which is fine for a booking
 * form and useless for a purchase date — property and art are routinely decades
 * old. The select scrolls, so the cost is a longer list, not a worse one.
 */
const YEAR_OPTIONS = computed(() => {
  const current = placeholder.value.year
  return Array.from({ length: 111 }, (_, index) => current - 100 + index)
})
</script>

<template>
  <CalendarRoot
    v-slot="{ grid, weekDays }"
    v-model:placeholder="placeholder"
    v-bind="forwarded"
    :class="cn('p-3 font-ds', props.class)"
  >
    <CalendarHeader>
      <CalendarHeading class="flex w-full items-center justify-center gap-1">
        <Select
          :default-value="placeholder.month.toString()"
          @update:open="value => isMonthOpen = value"
          @update:model-value="setMonth"
        >
          <SelectTrigger
            aria-label="Select month"
            class="w-auto h-8 gap-1 px-2 border-transparent bg-transparent font-bold"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent class="max-h-52">
            <SelectItem
              v-for="month in MONTH_OPTIONS"
              :key="month.value"
              :value="month.value.toString()"
            >
              {{ month.label }}
            </SelectItem>
          </SelectContent>
        </Select>

        <Select
          :default-value="placeholder.year.toString()"
          @update:open="value => isYearOpen = value"
          @update:model-value="setYear"
        >
          <SelectTrigger
            aria-label="Select year"
            class="w-auto h-8 gap-1 px-2 border-transparent bg-transparent font-bold"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent class="max-h-52">
            <SelectItem
              v-for="yearValue in YEAR_OPTIONS"
              :key="yearValue"
              :value="yearValue.toString()"
            >
              {{ yearValue }}
            </SelectItem>
          </SelectContent>
        </Select>
      </CalendarHeading>
    </CalendarHeader>

    <div class="flex flex-col gap-y-4 mt-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
      <CalendarGrid
        v-for="month in grid"
        :key="month.value.toString()"
      >
        <CalendarGridHead>
          <CalendarGridRow>
            <CalendarHeadCell
              v-for="day in weekDays"
              :key="day"
            >
              {{ day }}
            </CalendarHeadCell>
          </CalendarGridRow>
        </CalendarGridHead>
        <CalendarGridBody class="grid">
          <CalendarGridRow
            v-for="(weekDates, index) in month.rows"
            :key="`weekDate-${index}`"
            class="mt-2 w-full"
          >
            <CalendarCell
              v-for="weekDate in weekDates"
              :key="weekDate.toString()"
              :date="weekDate"
            >
              <CalendarCellTrigger
                :day="weekDate"
                :month="month.value"
              />
            </CalendarCell>
          </CalendarGridRow>
        </CalendarGridBody>
      </CalendarGrid>
    </div>
  </CalendarRoot>
</template>
